import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { CardScreen, InstructionStep } from './common/CardScreen';
import { t } from '@/strings/index';
import { ROUTES } from '@/constants/routes';
import authLogo from '@/assets/companyportal.png';
import { logScreenRender, logButtonClick, logBackNavigation, collectCompanyPortalStatus, endPolicyEvaluationTiming } from '../telemetry/telemetryEvents';

const COMPANY_PORTAL_URL = "https://play.google.com/store/apps/details?id=com.microsoft.windowsintune.companyportal";

const CompanyPortalInstall = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const platform = (location.state as any)?.selectedDevice || localStorage.getItem("selectedDevice");

  // Check if this is the first screen after auth redirect
  const isFirstScreenAfterAuth = (location.state as any)?.isFirstScreenAfterAuth ?? false;

  useEffect(() => {
    // Build UserBI data with MAM and MFA policy details
    const userbiData: Record<string, any> = {};
    
    const mfaResult = (location.state as any)?.mfaResult;
    const mamResult = (location.state as any)?.mamResult;
    
    // Include MFA policy data if available
    if (mfaResult) {
      if (typeof mfaResult.isRequired === 'boolean') {
        userbiData.mfaPolicyIsRequired = mfaResult.isRequired;
      }
      if (mfaResult.policyType) {
        userbiData.mfaPolicyType = mfaResult.policyType;
      }
      if (mfaResult.reason) {
        userbiData.mfaPolicyReason = mfaResult.reason;
      }
      if (mfaResult.details) {
        userbiData.mfaPolicyDetails = mfaResult.details;
      }
    }
    
    // Include MAM policy data if available
    if (mamResult) {
      if (typeof mamResult.isRequired === 'boolean') {
        userbiData.mamPolicyIsRequired = mamResult.isRequired;
      }
      if (mamResult.policyType) {
        userbiData.mamPolicyType = mamResult.policyType;
      }
      if (mamResult.reason) {
        userbiData.mamPolicyReason = mamResult.reason;
      }
      if (mamResult.details) {
        userbiData.mamPolicyDetails = mamResult.details;
      }
    }
    
    // Log screen view (UserBI + marks 'company-portal' step)
    logScreenRender('CompanyPortalInstall', Object.keys(userbiData).length > 0 ? userbiData : undefined);
    // Collect company portal status - assuming MAM is enabled if user reaches this screen
    collectCompanyPortalStatus(true);
    // End MAM evaluation timing (screen rendered after MAM API call)
    endPolicyEvaluationTiming('mam');
  }, [location.state]);

  const storeUrl = platform === 'android'
    ? COMPANY_PORTAL_URL
      : '';

  const handleNext = () => {
    logButtonClick('CompanyPortalInstall', 'next');
    // Continue to app download screen with platform info preserved
    navigate(ROUTES.APP_DOWNLOAD, {
      state: {
        ...location.state,
        completedCompanyPortal: true
      }
    });
  };

  const handleBack = () => {
    logBackNavigation('CompanyPortalInstall');
    navigate(-1);
  };

  const instructions: InstructionStep[] = [
    { number: 1, text: t('portalInstall.step1') },
    { number: 2, text: t('portalInstall.step2') }
  ];

  return (
    <CardScreen
      title={t('portalInstall.title')}
      qrCodeUrl={storeUrl}
      qrLogoUrl={authLogo}
      instructions={instructions}
      primaryButtonText={t('portalInstall.next')}
      onPrimaryButtonClick={handleNext}
      secondaryLinkText={t('portalInstall.cantScan')}
      secondaryLinkUrl={storeUrl}
      onBackClick={handleBack}
      logoAltText={t('footer.help')}
      showBackButton={!isFirstScreenAfterAuth}
    />
  );
};

export default CompanyPortalInstall;
